import { Filter, SortAsc, SortDesc, Search, X, Glasses } from 'lucide-react';

type IssueCategory = 'ACCESSIBILITY' | 'EAA' | 'SEO' | 'BROKEN_LINK' | 'TOXIC_LINK';
type IssueSeverity = 'critical' | 'serious' | 'moderate' | 'minor';
type IssueSortField = 'SEVERITY' | 'PAGE_URL' | 'CATEGORY' | 'TYPE';
type SortOrder = 'ASC' | 'DESC';

export interface IssueFiltersState {
  severity: IssueSeverity[];
  category: IssueCategory[];
  pageUrl: string;
  search: string;
  wcagLevel: string[];
  /** Show only items flagged for manual review (client-side; not a server filter). */
  manualReview: boolean;
}

interface IssueFiltersProps {
  filters: IssueFiltersState;
  onFiltersChange: (filters: IssueFiltersState) => void;
  sort: { field: IssueSortField; order: SortOrder };
  onSortChange: (sort: { field: IssueSortField; order: SortOrder }) => void;
  totalCount: number;
  isLoading: boolean;
}

const SEVERITY_OPTIONS: { value: IssueSeverity; label: string; colorClass: string }[] = [
  { value: 'critical', label: 'Critical', colorClass: 'bg-severity-critical' },
  { value: 'serious', label: 'Serious', colorClass: 'bg-severity-serious' },
  { value: 'moderate', label: 'Moderate', colorClass: 'bg-severity-moderate' },
  { value: 'minor', label: 'Minor', colorClass: 'bg-severity-minor' },
];

const CATEGORY_OPTIONS: { value: IssueCategory; label: string }[] = [
  { value: 'ACCESSIBILITY', label: 'Accessibility' },
  { value: 'EAA', label: 'EAA Compliance' },
  { value: 'SEO', label: 'SEO' },
  { value: 'BROKEN_LINK', label: 'Broken Links' },
  { value: 'TOXIC_LINK', label: 'Toxic Links' },
];

const SORT_OPTIONS: { value: IssueSortField; label: string }[] = [
  { value: 'SEVERITY', label: 'Severity' },
  { value: 'PAGE_URL', label: 'Page URL' },
  { value: 'CATEGORY', label: 'Category' },
  { value: 'TYPE', label: 'Issue Type' },
];

export function IssueFilters({
  filters,
  onFiltersChange,
  sort,
  onSortChange,
  totalCount,
  isLoading,
}: IssueFiltersProps) {
  const hasActiveFilters =
    filters.severity.length > 0 ||
    filters.category.length > 0 ||
    filters.search ||
    filters.pageUrl ||
    filters.wcagLevel.length > 0 ||
    filters.manualReview;

  // Manual review is an exclusive mode: it doesn't mix with severity/category.
  // While it's on, the severity/category chips are disabled; while any severity or
  // category is picked, the Manual review chip is disabled.
  const manualReviewActive = filters.manualReview;
  const severityOrCategoryActive = filters.severity.length > 0 || filters.category.length > 0;

  const toggleSeverity = (severity: IssueSeverity) => {
    const newSeverity = filters.severity.includes(severity)
      ? filters.severity.filter((s) => s !== severity)
      : [...filters.severity, severity];
    onFiltersChange({ ...filters, severity: newSeverity });
  };

  const toggleCategory = (category: IssueCategory) => {
    const newCategory = filters.category.includes(category)
      ? filters.category.filter((c) => c !== category)
      : [...filters.category, category];
    onFiltersChange({ ...filters, category: newCategory });
  };

  const clearFilters = () => {
    onFiltersChange({
      severity: [],
      category: [],
      pageUrl: '',
      search: '',
      wcagLevel: [],
      manualReview: false,
    });
  };

  const toggleSortOrder = () => {
    onSortChange({ ...sort, order: sort.order === 'ASC' ? 'DESC' : 'ASC' });
  };

  return (
    <div className="card p-4 space-y-4">
      {/* Header with result count */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Filter size={18} className="text-gray-medium" />
          <span className="text-body font-medium">Filters</span>
          {!isLoading && (
            <span className="text-body-sm text-gray-medium">
              ({totalCount} issue{totalCount !== 1 ? 's' : ''})
            </span>
          )}
        </div>
        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="text-body-sm text-coral hover:text-coral/80 flex items-center gap-1"
          >
            <X size={14} />
            Clear all
          </button>
        )}
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-medium" />
        <input
          type="text"
          placeholder="Search issues..."
          value={filters.search}
          onChange={(e) => onFiltersChange({ ...filters, search: e.target.value })}
          className="input pl-10"
        />
      </div>

      {/* Filter chips */}
      <div className="flex flex-wrap gap-4">
        {/* Severity filters */}
        <div>
          <label className="text-body-sm text-gray-medium mb-2 block">Severity</label>
          <div className="flex flex-wrap gap-2">
            {SEVERITY_OPTIONS.map((option) => (
              <button
                key={option.value}
                onClick={() => toggleSeverity(option.value)}
                disabled={manualReviewActive}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-body-sm border transition-colors ${
                  filters.severity.includes(option.value)
                    ? 'border-coral bg-coral/10 text-coral'
                    : manualReviewActive
                      ? 'border-gray-light text-gray-medium opacity-40 cursor-not-allowed'
                      : 'border-gray-light text-gray-medium hover:border-gray-medium'
                }`}
              >
                <span className={`w-2 h-2 rounded-full ${option.colorClass}`} />
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* Category filters */}
        <div>
          <label className="text-body-sm text-gray-medium mb-2 block">Category</label>
          <div className="flex flex-wrap gap-2">
            {CATEGORY_OPTIONS.map((option) => (
              <button
                key={option.value}
                onClick={() => toggleCategory(option.value)}
                disabled={manualReviewActive}
                className={`px-3 py-1.5 rounded-full text-body-sm border transition-colors ${
                  filters.category.includes(option.value)
                    ? 'border-coral bg-coral/10 text-coral'
                    : manualReviewActive
                      ? 'border-gray-light text-gray-medium opacity-40 cursor-not-allowed'
                      : 'border-gray-light text-gray-medium hover:border-gray-medium'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* Review status filter */}
        <div>
          <label className="text-body-sm text-gray-medium mb-2 block">Review</label>
          <button
            onClick={() => onFiltersChange({ ...filters, manualReview: !filters.manualReview })}
            disabled={severityOrCategoryActive}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-body-sm border transition-colors ${
              filters.manualReview
                ? 'border-coral bg-coral/10 text-coral'
                : severityOrCategoryActive
                  ? 'border-gray-light text-gray-medium opacity-40 cursor-not-allowed'
                  : 'border-gray-light text-gray-medium hover:border-gray-medium'
            }`}
          >
            <Glasses size={14} />
            Manual review
          </button>
        </div>
      </div>

      {/* Page URL filter */}
      <div>
        <label className="text-body-sm text-gray-medium mb-2 block">Page URL (contains)</label>
        <input
          type="text"
          placeholder="Filter by page URL..."
          value={filters.pageUrl}
          onChange={(e) => onFiltersChange({ ...filters, pageUrl: e.target.value })}
          className="input"
        />
      </div>

      {/* Sort controls */}
      <div className="flex items-center gap-4 pt-2 border-t border-gray-light">
        <span className="text-body-sm text-gray-medium">Sort by:</span>
        <select
          value={sort.field}
          onChange={(e) => onSortChange({ ...sort, field: e.target.value as IssueSortField })}
          className="input py-1.5 px-3 w-auto"
        >
          {SORT_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <button
          onClick={toggleSortOrder}
          className="flex items-center gap-1 text-body-sm text-gray-medium hover:text-dark-text"
          title={sort.order === 'ASC' ? 'Ascending' : 'Descending'}
        >
          {sort.order === 'ASC' ? (
            <>
              <SortAsc size={16} />
              <span>Asc</span>
            </>
          ) : (
            <>
              <SortDesc size={16} />
              <span>Desc</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
}
